/**
 * NOBL Gift Club Slot Machine
 * ----------------------------------------------------------------
 * All copy, colors, and behavior config are read from data-* attributes
 * on the section root, and prize data is read from a JSON script tag
 * inside it (see sections/slot-machine-promo.liquid). This file contains
 * no hardcoded merchant-facing values, so it works unchanged for any
 * number of section instances on a page, including inside the theme
 * editor where sections can be added, removed, or re-rendered.
 *
 * Game mechanic: ONE spin reveals every prize in sequence. Reel 0
 * lands first, then each following reel lands `reelStaggerSeconds`
 * later. Every landing updates the heading/subheading above the
 * button, the counter, and unlocks the matching gift-collection card.
 * The heading/subheading at the very top of the card are static -
 * they're filled in once on load (so their [n]/[v] tokens resolve)
 * and are never touched again by game state. Once the last reel
 * lands, the button becomes "Continue" and redirects to a destination
 * URL or straight to checkout with every prize added to cart. State
 * persists across reloads via localStorage.
 */
(function () {
  "use strict";

  var SELECTOR_ROOT = "[data-slot-promo]";
  var FILLER_COUNT = 20; // filler tags rendered before the winning result on each reel

  function initAll() {
    var roots = document.querySelectorAll(SELECTOR_ROOT);
    for (var i = 0; i < roots.length; i++) {
      initSlotPromo(roots[i]);
    }
  }

  function initSlotPromo(root) {
    if (!root || root.dataset.slotPromoBound) return;
    root.dataset.slotPromoBound = "true";

    var config = readConfig(root);
    var prizes = readPrizes(root);
    if (!prizes.length) return;

    var maxReels = config.maxReels;
    if (!maxReels || maxReels > prizes.length) maxReels = prizes.length;

    var cabinet = root.querySelector("[data-slot-cabinet]");
    var frame = root.querySelector("[data-slot-frame]");
    var lever = root.querySelector("[data-slot-lever]");
    var reels = Array.prototype.slice.call(root.querySelectorAll("[data-slot-reel]"));
    var button = root.querySelector("[data-slot-button]");
    var btnText = root.querySelector("[data-slot-btn-text]");
    var staticHeadingEl = root.querySelector("[data-slot-static-heading]");
    var staticSubheadingEl = root.querySelector("[data-slot-static-subheading]");
    var headingEl = root.querySelector("[data-slot-heading]");
    var subheadingEl = root.querySelector("[data-slot-subheading]");
    var counterEl = root.querySelector("[data-slot-counter]");
    var footerCaptionEl = root.querySelector("[data-slot-footer-caption]");
    var gridCounterEl = root.querySelector("[data-slot-grid-counter]");
    var gridItems = Array.prototype.slice.call(root.querySelectorAll("[data-slot-grid-item]"));
    var prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    var totalValue = sumPrizeValues(prizes);
    var totalValueDisplay = config.currencySymbol + totalValue.toLocaleString();

    var state = {
      revealCount: 0,
      complete: false,
      spinning: false,
      startFired: false,
      endFired: false,
      revealed: []
    };

    // Static heading/subheading at the top of the card: filled in once so
    // their [n]/[v] tokens resolve, then never updated again by game state.
    if (staticHeadingEl) staticHeadingEl.textContent = config.topHeading;
    if (staticSubheadingEl) {
      staticSubheadingEl.textContent = fillTemplate(config.topSubheadingTemplate, {
        n: maxReels,
        v: totalValueDisplay
      });
    }

    // build each reel's strip once: filler tags + the actual prize
    // result (image + Free badge) pinned as the final item.
    reels.forEach(function (reelEl, i) {
      buildStrip(reelEl, config.fillerTagText, config.fillerTagLogoUrl, prizes[i]);
    });

    setFooterCaption(maxReels);
    setGridCounter(0, maxReels);
    setIdleState(maxReels);

    var storedResult = readStoredResult(config.storageKey);
    if (storedResult && Array.isArray(storedResult.revealed) && storedResult.revealed.length === maxReels) {
      restoreCompletedState();
    }

    if (cabinet) {
      cabinet.addEventListener("click", handleSpin);
      cabinet.addEventListener("keydown", function (event) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleSpin();
        }
      });
    }
    if (button) {
      button.addEventListener("click", handleSpin);
    }

    function handleSpin() {
      if (state.complete) {
        navigateToDestination(config, state.revealed);
        return;
      }
      if (state.spinning) return;

      state.spinning = true;
      if (!state.startFired) {
        state.startFired = true;
        emit("slot_game_start");
      }

      setSpinningState();
      if (cabinet) cabinet.classList.add("is-disabled");
      if (button) button.disabled = true;

      if (prefersReducedMotion) {
        // Skip the spin theatre entirely; land everything at once.
        reels.forEach(function (reelEl) {
          landReelInstant(reelEl);
          reelEl.classList.add("is-landed");
        });
        prizes.slice(0, maxReels).forEach(function (prize, i) {
          revealOne(prize, i + 1, maxReels);
        });
        finish();
        return;
      }

      var firstMs = config.firstReelMs;
      var staggerMs = config.reelStaggerMs;

      for (var i = 0; i < maxReels; i++) {
        (function (index) {
          var durationMs = firstMs + index * staggerMs;
          spinReel(reels[index], durationMs);

          window.setTimeout(function () {
            reels[index].classList.remove("is-spinning");
            if (prizes[index] && prizes[index].highlightGift) {
              reels[index].classList.add("slot-promo__reel--highlight");
            }
            lightUpReel(reels[index]);
            if (lever) {
              lever.classList.add("is-pulled");
              window.setTimeout(function () {
                lever.classList.remove("is-pulled");
              }, 250);
            }
            revealOne(prizes[index], index + 1, maxReels);

            if (index === maxReels - 1) {
              finish();
            }
          }, durationMs);
        })(i);
      }
    }

    function lightUpReel(reelEl) {
      reelEl.classList.add("is-landed", "is-flash");
      if (frame) frame.classList.add("is-flash");
      window.setTimeout(function () {
        reelEl.classList.remove("is-flash");
        if (frame) frame.classList.remove("is-flash");
      }, 700);
    }

    function revealOne(prize, revealIndex, total) {
      state.revealCount = revealIndex;
      state.revealed.push({ spinIndex: revealIndex - 1, prize: prize });

      setCounter(revealIndex, total);
      setGridCounter(revealIndex, total);
      unlockGridItem(revealIndex - 1);

      setHeadingSubheading(
        fillTemplate(config.revealHeadingTemplate, { name: prize.name || "" }),
        fillTemplate(config.revealSubheadingTemplate, {
          i: revealIndex,
          n: total,
          v: totalValueDisplay
        })
      );
    }

    function finish() {
      state.complete = true;
      state.spinning = false;

      setHeadingSubheading(
        fillTemplate(config.completeHeadingTemplate, { n: maxReels, v: totalValueDisplay }),
        fillTemplate(config.completeSubheadingTemplate, { n: maxReels, v: totalValueDisplay })
      );

      if (cabinet) cabinet.classList.remove("is-disabled");
      if (button) {
        button.disabled = false;
        button.classList.remove("is-spinning");
        button.classList.add("is-complete");
      }
      if (btnText) btnText.textContent = config.buttonCompleteText;

      persistResult(config.storageKey, state.revealed);

      if (config.confettiEnabled && !prefersReducedMotion) {
        launchConfetti(config.confettiColors);
      }

      if (!state.endFired) {
        state.endFired = true;
        emit("slot_game_complete");
      }
    }

    function restoreCompletedState() {
      state.complete = true;
      state.revealed = storedResult.revealed;
      state.revealCount = maxReels;

      if (lever) lever.classList.remove("is-hint");

      reels.forEach(function (reelEl, i) {
        landReelInstant(reelEl);
        reelEl.classList.add("is-landed");
        if (prizes[i] && prizes[i].highlightGift) {
          reelEl.classList.add("slot-promo__reel--highlight");
        }
      });

      gridItems.forEach(function (item, i) {
        if (i < maxReels) item.classList.add("is-unlocked");
      });

      setCounter(maxReels, maxReels);
      setGridCounter(maxReels, maxReels);

      setHeadingSubheading(
        fillTemplate(config.completeHeadingTemplate, { n: maxReels, v: totalValueDisplay }),
        fillTemplate(config.completeSubheadingTemplate, { n: maxReels, v: totalValueDisplay })
      );

      if (cabinet) cabinet.classList.add("is-disabled");
      if (button) {
        button.classList.remove("is-spinning");
        button.classList.add("is-complete");
      }
      if (btnText) btnText.textContent = config.buttonCompleteText;
    }

    function setIdleState(total) {
      if (button) {
        button.classList.remove("is-spinning", "is-complete");
      }
      if (btnText) btnText.textContent = config.buttonIdleText;
      if (lever) lever.classList.add("is-hint");
      setCounter(0, total);
      setHeadingSubheading(
        config.idleHeading,
        fillTemplate(config.idleSubheadingTemplate, { n: total, v: totalValueDisplay })
      );
    }

    function setSpinningState() {
      if (button) button.classList.add("is-spinning");
      if (btnText) btnText.textContent = config.buttonSpinningText;
      if (lever) lever.classList.remove("is-hint");
      setCounter(0, maxReels);
      setHeadingSubheading(
        config.spinningHeading,
        fillTemplate(config.spinningSubheadingTemplate, { n: maxReels, v: totalValueDisplay })
      );
    }

    function setHeadingSubheading(heading, subheading) {
      if (headingEl) headingEl.textContent = heading;
      if (subheadingEl) subheadingEl.textContent = subheading;
    }

    function setCounter(i, n) {
      if (!counterEl) return;
      counterEl.textContent = i === 0 && !state.startFired ? config.readyText : i + " / " + n;
    }

    function setFooterCaption(n) {
      if (!footerCaptionEl) return;
      footerCaptionEl.textContent = fillTemplate(config.footerCaptionTemplate, { n: n });
    }

    function setGridCounter(i, n) {
      if (!gridCounterEl) return;
      gridCounterEl.textContent = fillTemplate(config.gridCounterTemplate, { i: i, n: n });
    }

    function unlockGridItem(index) {
      var item = gridItems[index];
      if (item) item.classList.add("is-unlocked");
    }
  }

  function buildStrip(reelEl, fillerText, fillerLogoUrl, prize) {
    var strip = reelEl.querySelector("[data-slot-strip]");
    if (!strip) return;
    strip.innerHTML = "";

    for (var i = 0; i < FILLER_COUNT; i++) {
      var tag = document.createElement("div");
      tag.className = "slot-promo__tag";
      var shape = document.createElement("span");
      shape.className = "slot-promo__tag-shape";
      if (fillerLogoUrl) {
        shape.classList.add("slot-promo__tag-shape--logo");
        shape.innerHTML = '<img src="' + escapeAttribute(fillerLogoUrl) + '" alt="">';
      } else {
        shape.textContent = fillerText || "NOBL";
      }
      tag.appendChild(shape);
      strip.appendChild(tag);
    }

    var result = document.createElement("div");
    result.className = "slot-promo__result";

    if (!prize || !prize.hideFreeText) {
      var badge = document.createElement("span");
      badge.className = "slot-promo__result-badge";
      if (prize && prize.highlightGift) {
        badge.classList.add("slot-promo__result-badge--highlight");
      }
      badge.appendChild(document.createTextNode("Free"));
      if (prize && prize.value) {
        var priceSpan = document.createElement("span");
        priceSpan.className = "slot-promo__grid-price-value";
        priceSpan.textContent = prize.value;
        badge.appendChild(priceSpan);
      }
      result.appendChild(badge);
    }

    var imgWrap = document.createElement("div");
    imgWrap.className = "slot-promo__result-img";
    if (prize && prize.image) {
      imgWrap.innerHTML = '<img src="' + escapeAttribute(prize.image) + '" alt="">';
    }
    result.appendChild(imgWrap);

    var nameEl = document.createElement("p");
    nameEl.className = "slot-promo__grid-name";
    nameEl.textContent = (prize && prize.name) || "";
    result.appendChild(nameEl);

    strip.appendChild(result);
  }

  function landReelInstant(reelEl) {
    var strip = reelEl.querySelector("[data-slot-strip]");
    if (!strip) return;
    var itemH = reelEl.getBoundingClientRect().height;
    strip.style.transition = "none";
    strip.style.transform = "translateY(" + -(FILLER_COUNT * itemH) + "px)";
  }

  function spinReel(reelEl, durationMs) {
    var strip = reelEl.querySelector("[data-slot-strip]");
    if (!strip) return;
    var itemH = reelEl.getBoundingClientRect().height;
    var targetY = -(FILLER_COUNT * itemH);

    reelEl.classList.add("is-spinning");
    strip.style.transition = "none";
    strip.style.transform = "translateY(0)";
    void strip.offsetHeight; // force reflow so the animated transition below applies cleanly

    window.requestAnimationFrame(function () {
      strip.style.transition = "transform " + durationMs + "ms cubic-bezier(0.15, 0.85, 0.35, 1)";
      strip.style.transform = "translateY(" + targetY + "px)";
    });
  }

  function fillTemplate(template, tokens) {
    var result = template || "";
    Object.keys(tokens).forEach(function (key) {
      result = result.split("[" + key + "]").join(tokens[key]);
    });
    return result;
  }

  function readConfig(root) {
    var dataset = root.dataset;
    return {
      maxReels: parseInt(dataset.maxReels, 10) || 0,
      destinationUrl: dataset.destinationUrl || "",
      redirectToCheckout: dataset.redirectToCheckout === "true",
      storageKey: dataset.storageKey || "nobl_gift_club_won",
      currencySymbol: dataset.currencySymbol || "$",
      confettiEnabled: dataset.enableConfetti === "true",
      confettiColors: (dataset.confettiColors || "")
        .split(",")
        .map(function (c) { return c.trim(); })
        .filter(Boolean),
      firstReelMs: (parseFloat(dataset.firstReelSeconds) || 2.5) * 1000,
      reelStaggerMs: (parseFloat(dataset.reelStaggerSeconds) || 0.9) * 1000,
      fillerTagText: dataset.fillerTagText || "NOBL",
      fillerTagLogoUrl: dataset.fillerTagLogoUrl || "",
      readyText: dataset.readyText || "Ready",
      topHeading: dataset.topHeading || "",
      topSubheadingTemplate: dataset.topSubheadingTemplate || "",
      idleHeading: dataset.idleHeading || "",
      idleSubheadingTemplate: dataset.idleSubheadingTemplate || "",
      spinningHeading: dataset.spinningHeading || "",
      spinningSubheadingTemplate: dataset.spinningSubheadingTemplate || "",
      revealHeadingTemplate: dataset.revealHeadingTemplate || "[name] is yours!",
      revealSubheadingTemplate: dataset.revealSubheadingTemplate || "[i] of [n] free gifts revealed.",
      completeHeadingTemplate: dataset.completeHeadingTemplate || "",
      completeSubheadingTemplate: dataset.completeSubheadingTemplate || "",
      buttonIdleText: dataset.buttonIdleText || "Spin to Win",
      buttonSpinningText: dataset.buttonSpinningText || "Spinning...",
      buttonCompleteText: dataset.buttonCompleteText || "Continue",
      footerCaptionTemplate: dataset.footerCaptionTemplate || "[n] gifts. One lucky spin.",
      gridCounterTemplate: dataset.gridCounterTemplate || "[i] / [n] unlocked"
    };
  }

  function readPrizes(root) {
    var script = root.querySelector("script[data-slot-prizes]");
    if (!script) return [];
    try {
      var parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function sumPrizeValues(prizes) {
    return (prizes || []).reduce(function (sum, prize) {
      var numeric = parseFloat(String((prize && prize.value) || "").replace(/[^0-9.]/g, ""));
      return sum + (isNaN(numeric) ? 0 : numeric);
    }, 0);
  }

  function navigateToDestination(config, revealed) {
    if (config.redirectToCheckout) {
      addPrizesToCartAndRedirect(revealed);
      return;
    }
    if (config.destinationUrl) {
      window.location.href = config.destinationUrl;
    }
  }

  function addPrizesToCartAndRedirect(revealed) {
    var items = (revealed || [])
      .filter(function (entry) { return entry.prize && entry.prize.variantId; })
      .map(function (entry) { return { id: entry.prize.variantId, quantity: 1 }; });

    if (!items.length) {
      window.location.href = "/checkout";
      return;
    }

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: items })
    })
      .then(function () { window.location.href = "/checkout"; })
      .catch(function () { window.location.href = "/checkout"; });
  }

  function persistResult(storageKey, revealed) {
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ revealed: revealed, at: Date.now() })
      );
    } catch (error) {
      /* localStorage unavailable (private mode, etc.) - fail silently */
    }
  }

  function readStoredResult(storageKey) {
    try {
      var raw = window.localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function emit(eventName) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName });
    window.dispatchEvent(new CustomEvent(eventName));
  }

  function launchConfetti(colors) {
    if (!colors.length) return;
    var pieceCount = 120;

    for (var i = 0; i < pieceCount; i++) {
      (function (index) {
        var piece = document.createElement("div");
        piece.className = "slot-promo-confetti-piece";
        piece.style.left = Math.random() * 100 + "vw";
        piece.style.background = colors[index % colors.length];
        piece.style.animationDuration = 2.4 + Math.random() * 1.8 + "s";
        piece.style.animationDelay = Math.random() * 0.6 + "s";
        piece.style.display = "block";
        document.body.appendChild(piece);
        window.setTimeout(function () {
          piece.remove();
        }, 5200);
      })(i);
    }
  }

  function escapeAttribute(value) {
    return String(value).replace(/[&<>"'`]/g, function (char) {
      return (
        {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
          "`": "&#96;"
        }[char] || char
      );
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Re-init when Shopify's theme editor swaps section markup in place.
  document.addEventListener("shopify:section:load", function (event) {
    var root = event.target.querySelector(SELECTOR_ROOT);
    if (root) initSlotPromo(root);
  });
})();